import { useNavigate, useLocation } from 'react-router';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { NoContentContainer } from './no-content-container';
import { ChatScreenReducer } from '../../../../../utils/reducer';
import QueryContainer from './query-container/index';
import ContentContainer from './content-container/index';
// import { Rag } from '../../components';
import './chat.css';
import { usePageHeader } from '../../../user';
import {
  getAllPermissionsOfLlms,
  loadAllPermissionsOfLlms,
  LLM_PERMISSIONS_INVALIDATED_EVENT,
  pickDefaultChatModalForProvider,
  verifyAccess,
  type LlmPermissionItem,
} from '../../../../../services';
import { MakeToast } from '../../../../../components';
import {
  chatInitialStateProps,
  type ChatGenerationUiState,
  type ChatStreamControlHandle,
} from './chat-screen.props';
import { ChatScreenActionTypes } from '../../../../../utils/reducer-types';
import type { DataItem } from '../../../../../utils/interface-types';
import { sanitizeChatHistoryForHydration } from '../../../../../utils/interface-types';
import ChatHistoryPanel from '../../components/chat-history';
import ModelTabs from '../../components/models-tabs';
import { fetchPermittedChatModels, UpdateModelForProvider } from '../../services/chat';
import {
  hasActiveFeatureRequirements,
  mergeModelCapabilities,
  pickCompatibleModel,
} from '../../lib/model-capability-filter';
import {
  applyPickedChatModel,
  persistProviderFeatureFlags,
  persistWebSearchFlags,
  resolveWebSearchMode,
} from '../../lib/chat-model-persist';
import { ChatModelContext } from '../../../../../utils/context';
// import { ModuleTour } from '../../../../../components/tour/module-tour';
// import { useModuleTour } from '../../../../../components/tour/useModuleTour';
// import { MODULE_IDS } from '../../../../../components/tour/constants';
// import { getChatTourSteps } from '../../tour/steps';
// import ProviderTabs from '../../components/tabs';

const CHAT_ACTIVE_PROVIDER_ID_KEY = 'chatActiveProviderId';

/** Restore last chat tab after navigating away (React state is lost on unmount). */
function resolveChatProviderIdFromStorage(perms: LlmPermissionItem[]): number | null {
  if (!perms?.length) return null;

  const rawId = localStorage.getItem(CHAT_ACTIVE_PROVIDER_ID_KEY);
  const storedId = rawId != null && rawId !== '' ? Number(rawId) : NaN;
  if (Number.isFinite(storedId) && perms.some((x) => x.proverID === storedId)) {
    return storedId;
  }

  const storedName = localStorage.getItem('activeProvider')?.trim();
  if (storedName) {
    const byName = perms.find((x) => x.provider_name?.toLowerCase() === storedName.toLowerCase());
    if (byName) return byName.proverID;
  }

  const openai = perms.find((x) => x.provider_name?.toLowerCase() === 'openai');
  return openai?.proverID ?? perms[0]?.proverID ?? null;
}

type ChatPageHeaderTabsProps = {
  chatState: chatInitialStateProps;
  activeProvider: number | null;
  setActiveProvider: React.Dispatch<React.SetStateAction<number | null>>;
  dispatch: React.Dispatch<ChatScreenActionTypes>;
  setIsContentAvailable: React.Dispatch<React.SetStateAction<boolean>>;
  llmPermissions: LlmPermissionItem[];
  llmPermissionsLoaded: boolean;
};

function ChatPageHeaderTabs({
  chatState,
  activeProvider,
  setActiveProvider,
  dispatch,
  setIsContentAvailable,
  llmPermissions,
  llmPermissionsLoaded,
}: ChatPageHeaderTabsProps) {
  return (
    <div className="flex items-center gap-3">
      <div data-tour="chat-tour-header">
        <ModelTabs
          activeTab={activeProvider ?? 0}
          setActiveTab={setActiveProvider}
          setActiveProvider={setActiveProvider}
          dispatch={dispatch}
          activeProviderId={activeProvider ?? 0}
          state={chatState}
          setIsContentAvailable={setIsContentAvailable}
          onClose={() => dispatch({ type: 'SET_CHAT_HISTORY_OPEN', payload: false })}
          llmPermissions={llmPermissions}
          llmPermissionsLoaded={llmPermissionsLoaded}
          placement="header"
        />
      </div>
    </div>
  );
}

function Chat() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setChatModel } = useContext(ChatModelContext);
  const initialState = {
    rag: false,
    queryIndex: null,
    responseIndex: null,
    showQueryActions: false,
    showResponseActions: false,
    showEditQueryActions: false,
    queryValue: '',
    rows: 0,
    inputRows: 0,
    updateQuery: '',
    arrow: 'hidden',
    isCopied: false,
    resCopiedIndex: null,
    newResponseLoader: false,
    chatHistory: [],
    showAnswer: false,
    quesIndex: null,
    token: null,
    useDocumentOnly: false,
    chatHasDocument: false,
    chatHistoryOpen: false,
    setChatHistoryOpen: () => {},
    editedSession: null,
    activeSession: null,
    editSessionIndex: null,
    dataChanged: false,
    totalSessions: null,
    pinnedLoading: false,
    sessionsLoading: false,
    todaySessions: [],
    last7DaysSessions: [],
    pinnedSessions: [],
    pinSessionHistory: { search: '' },
    sessionHistory: { search: '', limit: 10, offset: 0 },
    chatScreenLoading: false,
    webSearchEnabled: false,
    webSearchMode: 'auto' as const,
    pendingFile: null,
    isFileUploadPending: false,
    thinkingEnabled: false,
  };

  const chatScreenRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const sendButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousProviderRef = useRef<number | null>(null);
  const [isContentAvailable, setIsContentAvailable] = useState<boolean>(false);
  const [, setShowError] = useState(false);
  const [activeProvider, setActiveProvider] = useState<number | null>(null);
  const [permissionsRefreshKey, setPermissionsRefreshKey] = useState(0);
  const [llmPermissions, setLlmPermissions] = useState<LlmPermissionItem[]>([]);
  const [llmPermissionsLoaded, setLlmPermissionsLoaded] = useState(false);
  const [generationUi, setGenerationUi] = useState<ChatGenerationUiState>({
    isGenerating: false,
  });
  const streamControlRef = useRef<ChatStreamControlHandle | null>(null);
  const [state, dispatch] = useReducer<React.Reducer<chatInitialStateProps, ChatScreenActionTypes>>(
    ChatScreenReducer,
    initialState,
  );

  const handleGenerationUiChange = useCallback((ui: ChatGenerationUiState) => {
    setGenerationUi((prev) => (prev.isGenerating === ui.isGenerating ? prev : ui));
  }, []);

  const chatStateRef = useRef(state);
  chatStateRef.current = state;

  useEffect(() => {
    let cancelled = false;
    setLlmPermissionsLoaded(false);
    loadAllPermissionsOfLlms().then(async () => {
      if (cancelled) return;
      const perms = getAllPermissionsOfLlms();
      try {
        const enriched = await fetchPermittedChatModels();
        setLlmPermissions(mergeModelCapabilities(perms, enriched));
      } catch {
        setLlmPermissions(
          perms.map((item) => ({
            ...item,
            supports_tools: true,
            supports_reasoning: true,
          })),
        );
      }
      setLlmPermissionsLoaded(true);
      const resolved = resolveChatProviderIdFromStorage(perms);
      if (resolved != null) {
        setActiveProvider((p) => (p != null ? p : resolved));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [permissionsRefreshKey]);

  useEffect(() => {
    if (activeProvider != null) {
      localStorage.setItem(CHAT_ACTIVE_PROVIDER_ID_KEY, String(activeProvider));
    }
  }, [activeProvider]);

  useEffect(() => {
    const onInvalidated = () => setPermissionsRefreshKey((k) => k + 1);
    window.addEventListener(LLM_PERMISSIONS_INVALIDATED_EVENT, onInvalidated);
    return () => window.removeEventListener(LLM_PERMISSIONS_INVALIDATED_EVENT, onInvalidated);
  }, []);

  // Check module access after SSO completes (skip while token still in URL – Protected is still loading permissions)
  useEffect(() => {
    if (new URLSearchParams(location.search).has('token')) return;
    const hasAccess = verifyAccess('chat', '/chat');
    if (!hasAccess) {
      navigate('/unauthorized');
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!activeProvider || !llmPermissionsLoaded) return () => {};

    let cancelled = false;
    (async () => {
      if (cancelled) return;

      type ChatStorageData = {
        modal?: unknown;
        chatData?: unknown[];
        use_document?: boolean;
        has_document?: boolean;
        file_id?: string;
        file_type?: string;
        webSearchEnabled?: boolean;
        webSearchMode?: 'auto' | 'on' | 'off';
        codeInterpreterEnabled?: boolean;
        thinkingEnabled?: boolean;
      };

      type ChatStorageEntry = {
        provider_id: number;
        data?: ChatStorageData;
        session_id?: string | null;
      };

      const copyPreservedChatConfig = (
        target: ChatStorageData | undefined,
        source: ChatStorageData | undefined,
      ): ChatStorageData => ({
        ...target,
        ...persistWebSearchFlags(resolveWebSearchMode(source)),
        codeInterpreterEnabled: Boolean(source?.codeInterpreterEnabled),
        thinkingEnabled: Boolean(source?.thinkingEnabled),
        use_document: Boolean(source?.use_document),
        has_document: Boolean(source?.has_document),
        file_id: source?.file_id ?? '',
        file_type: source?.file_type,
      });
      let currentParsedData: ChatStorageEntry[];
      try {
        currentParsedData = JSON.parse(localStorage.getItem('chat') || '[]');
      } catch (error) {
        MakeToast('error', 'Error parsing chat data from localStorage');
        throw new Error(`Invalid chat data format ${error}`);
      }

      const permissions = llmPermissions;
      const providerExists = currentParsedData.some((item) => item.provider_id === activeProvider);

      if (!providerExists) {
        const modal = pickDefaultChatModalForProvider(permissions, activeProvider);
        const previousProviderData = currentParsedData.find(
          (item) => item.provider_id === previousProviderRef.current,
        );
        const newProviderData: ChatStorageEntry = {
          provider_id: activeProvider,
          session_id: null,
          data: copyPreservedChatConfig(
            {
              chatData: [],
              has_document: false,
              modal,
              file_id: '',
            },
            previousProviderData?.data,
          ),
        };

        const updatedChatData = [...currentParsedData, newProviderData];
        localStorage.setItem('chat', JSON.stringify(updatedChatData));
        currentParsedData = updatedChatData;
      }

      const previousProviderId = previousProviderRef.current;
      if (previousProviderId != null && previousProviderId !== activeProvider && providerExists) {
        const previousProviderData = currentParsedData.find(
          (item) => item.provider_id === previousProviderId,
        );
        currentParsedData = currentParsedData.map((entry) =>
          entry.provider_id === activeProvider
            ? {
                ...entry,
                data: copyPreservedChatConfig(entry.data, previousProviderData?.data),
              }
            : entry,
        );
        localStorage.setItem('chat', JSON.stringify(currentParsedData));
      }

      const updatedTabData = currentParsedData.find(
        (entry) => entry.provider_id === activeProvider,
      );

      const providerConfig = updatedTabData?.data;
      if (providerConfig) {
        const webSearchMode = resolveWebSearchMode(providerConfig);
        dispatch({
          type: 'SET_WEB_SEARCH_MODE',
          payload: webSearchMode,
        });
        dispatch({
          type: 'SET_CODE_INTERPRETER_ENABLED',
          payload: Boolean(providerConfig.codeInterpreterEnabled),
        });
        dispatch({
          type: 'SET_THINKING_ENABLED',
          payload: Boolean(providerConfig.thinkingEnabled),
        });
        dispatch({ type: 'SET_USE_DOCUMENT_ONLY', payload: Boolean(providerConfig.use_document) });

        const featureCtx = {
          thinkingEnabled: Boolean(providerConfig.thinkingEnabled),
          webSearchEnabled: webSearchMode === 'on',
          codeInterpreterEnabled: Boolean(providerConfig.codeInterpreterEnabled),
          mcpActive: false,
        };
        if (hasActiveFeatureRequirements(featureCtx) && llmPermissions.length) {
          const currentModal = providerConfig.modal as { name?: string; id?: number } | undefined;
          const pick = pickCompatibleModel({
            models: llmPermissions,
            ctx: featureCtx,
            current: {
              id: currentModal?.id,
              name: currentModal?.name,
              providerId: activeProvider,
            },
          });
          if (pick.ok && pick.switched) {
            const applied = applyPickedChatModel({
              model: pick.model,
              currentProviderId: activeProvider,
              setChatModel,
              setActiveProvider,
            });
            const providerLabel = pick.model.provider_name ? ` (${pick.model.provider_name})` : '';
            MakeToast('success', `Switched to ${pick.model.name}${providerLabel}`);
            if (applied.sessionId && pick.model.name) {
              UpdateModelForProvider({
                sessionId: applied.sessionId,
                model: pick.model.name,
                type: 'Switch-Model',
              }).catch(() => {});
            }
            if (applied.switchedProvider) {
              previousProviderRef.current = activeProvider;
              return;
            }
          } else if (!pick.ok) {
            persistProviderFeatureFlags(activeProvider, {
              thinkingEnabled: false,
              ...persistWebSearchFlags('auto'),
              codeInterpreterEnabled: false,
            });
            dispatch({ type: 'SET_WEB_SEARCH_MODE', payload: 'auto' });
            dispatch({ type: 'SET_CODE_INTERPRETER_ENABLED', payload: false });
            dispatch({ type: 'SET_THINKING_ENABLED', payload: false });
          }
        }
      }

      const chatData = updatedTabData?.data?.chatData;
      if (!updatedTabData || !Array.isArray(chatData) || chatData.length === 0) {
        dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });
        setIsContentAvailable(false);
      } else {
        const rawChatData = chatData as DataItem[];
        const needsRepair = rawChatData.some(
          (msg) => msg.role === 'AI' && (msg.isThinking === true || msg.isStreaming === true),
        );
        const sanitizedChatData = needsRepair
          ? sanitizeChatHistoryForHydration(rawChatData)
          : rawChatData;
        if (needsRepair) {
          const repairedStorage = currentParsedData.map((entry) =>
            entry.provider_id === activeProvider
              ? {
                  ...entry,
                  data: {
                    ...entry.data,
                    chatData: sanitizedChatData,
                  },
                }
              : entry,
          );
          localStorage.setItem('chat', JSON.stringify(repairedStorage));
        }
        dispatch({ type: 'SET_CHAT_HISTORY', payload: sanitizedChatData });
        setIsContentAvailable(sanitizedChatData.length > 0);
      }

      dispatch({ type: 'SET_ACTIVE_PROVIDER', payload: activeProvider });
      dispatch({ type: 'SET_SHOW_EDIT_QUERY_ACTIONS', payload: false });
      dispatch({ type: 'SET_QUERY_VALUE', payload: '' });
      previousProviderRef.current = activeProvider;
    })();
    return () => {
      cancelled = true;
    };
  }, [activeProvider, llmPermissions, llmPermissionsLoaded, setChatModel]);

  useEffect(() => {
    setTimeout(() => {
      setShowError(false);
    }, 2000);
  }, []);

  useEffect(() => {
    const handleToggleChatHistory = (event: Event) => {
      const customEvent = event as CustomEvent<{ left: number; top: number }>;
      if (customEvent.detail) {
        // Store button position for panel positioning
        window.dispatchEvent(
          new CustomEvent('chatHistoryButtonPosition', {
            detail: customEvent.detail,
          }),
        );
      }
      dispatch({ type: 'SET_CHAT_HISTORY_OPEN', payload: !state.chatHistoryOpen });
    };

    window.addEventListener('toggleChatHistory', handleToggleChatHistory);
    return () => {
      window.removeEventListener('toggleChatHistory', handleToggleChatHistory);
    };
  }, [dispatch, state.chatHistoryOpen]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('chatHistoryOpenChanged', { detail: state.chatHistoryOpen }),
    );
  }, [state.chatHistoryOpen]);

  // const {
  //   run: runTour,
  //   endTour,
  //   startTour,
  // } = useModuleTour({
  //   moduleId: MODULE_IDS.CHAT,
  //   runOnFirstVisit: true,
  // });
  // const chatTourSteps = useMemo(() => getChatTourSteps(), []);

  const headerLeftContent = useMemo(
    () => (
      <ChatPageHeaderTabs
        chatState={state}
        activeProvider={activeProvider}
        setActiveProvider={setActiveProvider}
        dispatch={dispatch}
        setIsContentAvailable={setIsContentAvailable}
        llmPermissions={llmPermissions}
        llmPermissionsLoaded={llmPermissionsLoaded}
      />
    ),
    [activeProvider, llmPermissions, llmPermissionsLoaded, dispatch, setIsContentAvailable, state],
  );

  usePageHeader({ className: 'relative', headerLeftContent });

  return (
    <>
      {/* Commented out: Rag component now integrated into ModelTabs */}
      <div className="chat-container flex flex-col flex-1 w-full text-black min-h-0">
        <div
          className={`chat-container-inner flex flex-col w-full flex-1 min-h-0 ${
            isContentAvailable ? '' : 'justify-center pb-[12vh]'
          }`}
        >
          {/* <Rag dispatch={dispatch} state={state} activeProviderId={activeProvider} /> */}
          <div
            ref={chatScreenRef}
            data-tour="chat-tour-content"
            className={`bg-transparent ${
              isContentAvailable
                ? 'flex-1 min-h-0 overflow-y-auto thin-scrollbar'
                : 'flex-none overflow-hidden mb-6'
            }`}
          >
            {!isContentAvailable ? (
              <NoContentContainer />
            ) : (
              <ContentContainer
                chatScreenRef={chatScreenRef}
                state={state}
                dispatch={dispatch}
                sendButtonRef={sendButtonRef}
                activeProvider={activeProvider ?? 0}
                chatScreenLoading={state?.chatScreenLoading}
                onRetryGeneration={() => streamControlRef.current?.retry()}
              />
            )}
          </div>
          <div className="flex-shrink-0" data-tour="chat-tour-input">
            <QueryContainer
              inputRef={inputRef}
              state={state}
              dispatch={dispatch}
              setIsContentAvailable={setIsContentAvailable}
              sendButtonRef={sendButtonRef}
              setShowError={setShowError}
              activeProvider={activeProvider ?? 0}
              onClose={() => dispatch({ type: 'SET_CHAT_HISTORY_OPEN', payload: false })}
              llmPermissions={llmPermissions}
              llmPermissionsLoaded={llmPermissionsLoaded}
              setActiveProvider={setActiveProvider}
              streamControlRef={streamControlRef}
              onGenerationUiChange={handleGenerationUiChange}
              isGenerationActive={generationUi.isGenerating}
              onStopGeneration={() => streamControlRef.current?.stop()}
            />
          </div>
        </div>
      </div>
      <ChatHistoryPanel
        isOpen={state.chatHistoryOpen}
        onClose={() => dispatch({ type: 'SET_CHAT_HISTORY_OPEN', payload: false })}
        state={state}
        dispatch={dispatch}
        setIsContentAvailable={setIsContentAvailable}
        activeProviderId={activeProvider ?? 0}
        setActiveProvider={setActiveProvider}
        setActiveTab={setActiveProvider}
        llmPermissions={llmPermissions}
      />
      {/* <ModuleTour steps={chatTourSteps} run={runTour} onFinish={endTour} onSkip={endTour} /> */}
    </>
  );
}

export default Chat;
